import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { parseISO } from 'date-fns';
import { Box, Stack, Typography } from '@mui/material';

import StatusBadge from '@/shared/components/StatusBadge';
import { getTeamLogoUrl } from '@/shared/services/getTeamLogoUrl';
import { getGameStatusColor } from '@/shared/utils/getGameStatusColor';
import { useVenueAwareTimeFormatter } from '@/features/games/hooks/useVenueAwareTimeFormatter';
import type { Game } from '@/features/games/types/Games';

interface Props {
  game: Game;
  /**
   * Rendered in the right-hand cluster, to the left of the game time. Pages use
   * it for their own per-game state (a read-only chip, a resource badge, …).
   */
  endAdornment?: ReactNode;
}

/**
 * Pill copy for a `game.status`. The listing vocabulary ("FINAL" / "UPCOMING")
 * rather than the raw enum the Manage Games table prints — only the badge shape
 * is shared with that table, the hue comes from the same `getGameStatusColor`.
 */
const STATUS_LABELS: Record<string, string> = {
  recorded: 'FINAL',
  scheduled: 'UPCOMING',
  live: 'LIVE',
};

function getTeamColor(game: Game, side: 'home' | 'away'): string {
  const g = game as any;
  const colorField = side === 'home' ? g.homeTeamColor : g.awayTeamColor;
  if (colorField) return colorField;
  const team = side === 'home' ? g.homeTeam : g.awayTeam;
  return team?.teamColors?.find((tc: any) => tc.type === 'primary')?.hex_color || '#555';
}

/**
 * The game "envelope" that tops a per-game card: status chip / away team /
 * score or `@` / home team / time. Visually mirrors `GamesContentList`'s
 * GameRow so any page carrying per-game controls feels native to anyone
 * familiar with the games listing. The row is non-clickable and carries a
 * bottom divider, since a panel always sits directly beneath it.
 */
export default function GameHeaderRow({ game, endAdornment }: Props) {
  const { formatTime } = useVenueAwareTimeFormatter();

  const start = useMemo(() => {
    if (!game.startTimestamp) return new Date();
    return game.startTimestamp instanceof Date
      ? game.startTimestamp
      : parseISO(game.startTimestamp);
  }, [game.startTimestamp]);

  const formattedTime = useMemo(
    () => formatTime(start, { formatType: 'time-only', venueTimezone: null }),
    [start, formatTime],
  );

  const gameStatus = (game as any).status || 'recorded';
  const homeScore = (game as any).homeTeamScore;
  const awayScore = (game as any).awayTeamScore;

  return (
    <Box
      sx={{
        opacity: gameStatus === 'scheduled' ? 0.85 : 1,
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Status pill — same badge the Manage Games table renders. */}
      <Box sx={{ width: 100, flexShrink: 0, textAlign: 'center' }}>
        <StatusBadge
          label={STATUS_LABELS[gameStatus] ?? gameStatus}
          color={getGameStatusColor(gameStatus)}
          pulse={gameStatus === 'live'}
        />
      </Box>

      {/* Away team */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, width: 200, flexShrink: 0 }}>
        {/* minWidth 0 lets the name ellipsize instead of spilling onto the status pill. */}
        <Stack sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: gameStatus !== 'scheduled' && awayScore > homeScore ? 700 : 400,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: '#9797A5', textAlign: 'right',
              fontFamily: 'Sohne', fontSize: '12px', lineHeight: '18px',
            }}
          >
            {game.awayTeam.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: gameStatus !== 'scheduled' && awayScore > homeScore ? 700 : 400,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              textAlign: 'right',
            }}
          >
            {game.awayTeam.abbreviation}
          </Typography>
        </Stack>
        <Box
          sx={{
            width: 42, height: 42, borderRadius: 1,
            backgroundColor: getTeamColor(game, 'away'),
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={getTeamLogoUrl(game.awayTeam)}
            alt={game.awayTeam.abbreviation || game.awayTeam.name}
            sx={{ width: 32, height: 32, objectFit: 'contain' }}
          />
        </Box>
      </Box>

      {/* Score or @ */}
      <Box sx={{ width: 80, flexShrink: 0, textAlign: 'center' }}>
        {gameStatus === 'scheduled' ? (
          <Typography variant="body2" color="text.secondary">@</Typography>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {awayScore} - {homeScore}
          </Typography>
        )}
      </Box>

      {/* Home team */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 200, flexShrink: 0 }}>
        <Box
          sx={{
            width: 42, height: 42, borderRadius: 1,
            backgroundColor: getTeamColor(game, 'home'),
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={getTeamLogoUrl(game.homeTeam)}
            alt={game.homeTeam.abbreviation || game.homeTeam.name}
            sx={{ width: 32, height: 32, objectFit: 'contain' }}
          />
        </Box>
        <Stack sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: gameStatus !== 'scheduled' && awayScore > homeScore ? 700 : 400,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: '#9797A5', textAlign: 'left',
              fontFamily: 'Sohne', fontSize: '12px', lineHeight: '18px',
            }}
          >
            {game.homeTeam.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: gameStatus !== 'scheduled' && awayScore > homeScore ? 700 : 400,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              textAlign: 'left',
            }}
          >
            {game.homeTeam.abbreviation}
          </Typography>
        </Stack>
      </Box>

      {/* Time + page-supplied adornment */}
      <Box sx={{ flex: 1, textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
        {endAdornment}
        <Typography variant="body2" color="text.secondary">{formattedTime}</Typography>
      </Box>
    </Box>
  );
}
